// Tổng quan CTV (chế độ Quản lý) — doanh số, Shop trực thuộc, vòng trạng thái đơn của khoảng đang chọn
// (components/overview/parts.tsx). Mọi số từ /reports/agency-network (đơn tự bán + các Shop, KHÔNG tính đơn hủy).
// Cuối trang: tài khoản thêm gần đây (RecentUnits).

import { UpgradeBanner } from '@/components/upgrade/UpgradeBanner'
import { useQuery } from '@tanstack/react-query'
import { Store } from 'lucide-react'
import { get } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { hasAccess } from '@/lib/permissions'
import { AnnouncementBanners } from '@/components/announce/AnnouncementBanners'
import { countFormat } from '@/components/charts/chart-utils'
import type { StatusCounts } from '@/components/charts/statusSegments'
import { MoneyCard, OrdersCard, OverviewGrid, OverviewHead, RevenueHero, revenuePoints, rangePeriod, rangeQuery, useOverviewRange } from '@/components/overview/parts'
import { RecentUnits } from '@/components/overview/RecentUnits'

interface NetworkReport {
  summary: StatusCounts & {
    networkGMV: number
    totalOrders: number
    totalShopsCount: number
  }
  daily: { date: string; gmv: number; ordersCount: number }[]
  /** Chỉ khi khoảng là một ngày: 24 giờ (giờ VN) */
  hourly?: { hour: number; gmv: number; ordersCount: number }[]
}

export default function CtvDashboard() {
  const [range, setRange] = useOverviewRange()
  const { data: report, isLoading, isError, refetch } = useQuery<NetworkReport>({
    queryKey: ['reports.agency-network', range.from, range.to],
    queryFn: () => get<NetworkReport>(`/reports/agency-network?${rangeQuery(range)}`),
  })
  const s = report?.summary
  // Admin cũng mở được trang này (menu admin) nhưng không có cấp dưới; nhân viên cần quyền xem mạng lưới
  const auth = getAuthState()
  const me = auth.shops.find((x) => x.shopId === auth.activeShopId)
  const canUnits = (me?.tenantType === 'agency' || me?.tenantType === 'ctv') && hasAccess(me.role, '/network', me.tenantType, me.staff, me.workModes)
  const shops = s?.totalShopsCount ?? 0

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <OverviewHead range={range} onRange={setRange} />
      <AnnouncementBanners />
      <UpgradeBanner />

      <OverviewGrid
        revenue={
          <RevenueHero
            label="COD dự kiến"
            total={s?.networkGMV}
            points={revenuePoints(range, report?.daily, report?.hourly, (d) => d.gmv, (h) => h.gmv)}
            period={rangePeriod(range)}
            hours={range.from === range.last}
            loading={isLoading}
            error={isError}
            onRetry={() => refetch()}
          />
        }
        units={
          <MoneyCard
            tone="blue"
            icon={Store}
            title="Thành viên"
            value={countFormat.format(shops)}
            parts={[{ label: 'Shop', value: shops }]}
            format={(v) => countFormat.format(v)}
            link={{ to: '/network', label: 'Hệ thống' }}
            loading={isLoading}
            error={isError}
          />
        }
        orders={<OrdersCard counts={s} loading={isLoading} error={isError} onRetry={() => refetch()} />}
      />
      {canUnits && <RecentUnits />}
    </div>
  )
}
